// src/tenantAdmin/components/CompanyAdminsSection.jsx
//
// The company's ADMINISTRATORS (`customer_users` rows), managed from the Utenti tab below the workspace's users
// (v202 — it was the «Amministratori azienda» tab). A company admin administers the company (billing details,
// plan and purchases, workspaces, the other admins) AND, through an implicit claim, every workspace it owns.
//
// Props: `companyId` — the company that owns the workspace SELECTED in Utenti (useWorkspaceCompany); `onChanged`
// — called after every write that can move the company's seat count (add, remove), so the host re-reads its
// seat tile.
//
// ⛔ The id still goes through `isCustomerAdminOf` before ANY request, and the section renders NOTHING when it
// fails (absent, not greyed — a data section): an id the user does not administer answers 403
// CUSTOMER_ADMIN_REQUIRED, which raises the global permissions-drift banner and tells the user their rights
// changed when in fact WE aimed at the wrong company.
//
// ⭐ ONE role. Everyone added is sent as `admin` (AddCompanyAdminDialog asks for the address alone). The other
// company role, `viewer`, is accepted by the backend but grants nothing (`router.py` CustomerInfo) while it
// takes a seat. A legacy viewer row is still listed (removable); the guards count only `admin` rows.
//
// ⭐ TWO SUCCESSFUL OUTCOMES, read from the response `status` and never from the HTTP code alone: `attached`
// (the person is an administrator NOW) or `invitation_sent` (the row appears only once the invitation is
// accepted). An UNKNOWN status reads as the weaker, invited wording: claiming a membership that may not exist is
// the one mistake with no recovery. And `email_sent: false` means NOT DELIVERED — never "sent".
//
// ⚠️ The two guards (no remove on yourself, nor on the last ACTIVE admin) are a COURTESY: the server is the
// authority and answers 409 CANNOT_DEMOTE_SELF / CANNOT_REMOVE_LAST_CUSTOMER_ADMIN, which is worded here too.
// Our count can be stale (another admin was deactivated in another tab).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, UserPlus } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext.jsx';
import ConfirmDialog from '../../admin/components/ConfirmDialog.jsx';
import {
  listCompanyMembers,
  addCompanyMember,
  removeCompanyMember,
  correctCompanyMemberEmail,
} from '../companyApi.js';
import { membershipRefusalCopy } from '../membershipRefusal.js';
import { userActionErrorCopy } from '../userActionError.js';
import OutcomeDialog from '../../components/ui/OutcomeDialog.jsx';
import UserStatusPill from './UserStatusPill.jsx';
import CorrectEmailDialog from './CorrectEmailDialog.jsx';
import AddCompanyAdminDialog from './AddCompanyAdminDialog.jsx';
import { COLORS, FONT_SANS, FONT_DISPLAY, RADII, SEMANTIC, SHADOW } from '../../constants/theme.js';

const th = {
  padding: '12px 24px', textAlign: 'left',
  fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase',
  color: COLORS.textSecondary, fontFamily: FONT_SANS, whiteSpace: 'nowrap',
};
const td = {
  padding: '14px 24px', fontSize: '13px', color: COLORS.ink,
  fontFamily: FONT_SANS, borderTop: `1px solid ${COLORS.border}`,
};
const rowBtn = (variant) => ({
  padding: '8px 16px', borderRadius: RADII.control, fontSize: '12px', fontWeight: 600,
  fontFamily: FONT_SANS, whiteSpace: 'nowrap',
  ...(variant === 'danger'
    ? { border: 'none', background: SEMANTIC.errore.fg, color: COLORS.surface }
    : { border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface, color: COLORS.ink }),
});
const muted = (disabled) => ({
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
});
const addBtn = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  height: '46px', padding: '0 20px', borderRadius: RADII.control, border: 'none',
  background: COLORS.accent, color: COLORS.surface, fontSize: '13px', fontWeight: 600,
  fontFamily: FONT_SANS, cursor: 'pointer',
};

export default function CompanyAdminsSection({ companyId, onChanged }) {
  const { t } = useTranslation('admin');
  const { isCustomerAdminOf, user } = useAuth();

  // The company on screen NOW: an answer that lands after a switch belongs to the old company — no outcome, no
  // re-read.
  const companyRef = useRef(companyId);
  companyRef.current = companyId;
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  // ⛔ THE gate. Everything below reads it before touching the network.
  const allowed = !!companyId && !!isCustomerAdminOf?.(companyId);
  const currentUserId = user?.user_id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);

  // One ticket per read: two loads can be in flight at once (a workspace switch moves the company) and the slow
  // one is not always the old one. Anything that is not the latest is dropped.
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    if (!allowed) {
      setRows([]);
      setError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await listCompanyMembers(companyId);
      if (mine !== seq.current) return;
      setRows(data?.users || []);
    } catch (err) {
      if (mine !== seq.current) return;
      setRows([]);
      setError(userActionErrorCopy(t, err));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [allowed, companyId, t]);

  // A company change clears what belonged to the previous one BEFORE the next answer arrives.
  useEffect(() => {
    setRows([]);
    setOutcome(null);
    setAddOpen(false);
    setRemoveTarget(null);
    setEmailTarget(null);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // ── The two guards ──────────────────────────────────────────────────────────
  // ⚠️ Counted over the ACTIVE admins only: a deactivated admin administers nothing. The backend counts the same
  // way (409 CANNOT_REMOVE_LAST_CUSTOMER_ADMIN) and stays the authority.
  const activeAdmins = rows.filter(r => r.role === 'admin' && r.is_active !== false).length;
  const isLastActiveAdmin = (row) =>
    row.role === 'admin' && row.is_active !== false && activeAdmins <= 1;

  // Why a row's «Rimuovi» is locked, or '' when it is not.
  function lockReason(row) {
    if (row.user_id === currentUserId) return t('manage.members.self');
    if (isLastActiveAdmin(row)) return t('manage.members.lastAdmin');
    return '';
  }

  // ⛔ CANNOT_DEMOTE_SELF has no entry in userActionError.js's flat table (it is a company-level refusal), so it
  // is worded from the guard's own sentence — a stale count and a fresh click read alike.
  function removeRefusal(err) {
    if (err?.code === 'CANNOT_DEMOTE_SELF') return t('manage.members.self');
    return membershipRefusalCopy(t, err, 'remove') || userActionErrorCopy(t, err);
  }

  // ── Writes ──────────────────────────────────────────────────────────────────
  // Run by AddCompanyAdminDialog, which words a refusal (it rethrows here) and closes on success.
  async function submitAdd(next) {
    const at = companyId;
    const res = await addCompanyMember(at, { email: next, role: 'admin' });
    if (companyRef.current !== at) return;
    await load();
    if (changedRef.current) changedRef.current();
    // Close the dialog in the SAME render that opens the outcome, so the outcome never sits over it (the
    // dialog's own onClose that follows is then a no-op).
    setAddOpen(false);
    // ⭐ The outcome is the response `status`, never the HTTP code.
    if (res?.status === 'attached') {
      setOutcome({ tone: 'success', text: t('manage.members.added', { email: next }) });
    } else if (res?.email_sent === false) {
      // ⛔ Registered but NOT delivered — the person has no link. Never "sent".
      setOutcome({ tone: 'warning', text: t('manage.members.notDelivered') });
    } else {
      setOutcome({ tone: 'info', text: t('manage.members.invited', { email: next }) });
    }
  }

  // ConfirmDialog renders whatever we throw as its sentence, so the refusal is worded HERE and rethrown.
  async function confirmRemove() {
    if (!removeTarget) return;
    try {
      await removeCompanyMember(companyId, removeTarget.user_id);
    } catch (err) {
      throw new Error(removeRefusal(err));
    }
    await load();
    if (changedRef.current) changedRef.current();
  }

  // ⛔ Rethrows: CorrectEmailDialog owns the error surface. This only runs on the success path.
  async function submitCorrectEmail(nextEmail) {
    const row = emailTarget;
    const res = await correctCompanyMemberEmail(companyId, row.user_id, nextEmail);
    // The SERVER says which pair actually changed; the form only proposed one.
    const previous = res?.previous_email || row.email;
    const applied = res?.email || nextEmail;
    await load();
    setOutcome({ tone: 'success', text: t('manage.correctEmailDone', { previous, email: applied }) });
  }

  if (!allowed) return null;

  return (
    <div style={{
      marginTop: '24px', background: COLORS.surface, border: `1px solid ${COLORS.border}`,
      borderRadius: RADII.section, boxShadow: SHADOW.card, overflow: 'hidden', fontFamily: FONT_SANS,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '24px', flexWrap: 'wrap' }}>
        <div style={{
          width: '40px', height: '40px', borderRadius: RADII.pill, flexShrink: 0,
          border: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ShieldCheck size={20} color={COLORS.ink} strokeWidth={1.8} />
        </div>
        <div style={{ flex: 1, minWidth: '240px' }}>
          <h2 style={{ margin: 0, fontFamily: FONT_DISPLAY, fontSize: '16px', fontWeight: 700, lineHeight: '24px', color: COLORS.ink }}>
            {t('manage.members.title')}
          </h2>
          <div style={{ fontSize: '12px', color: COLORS.textTertiary }}>
            {t('manage.members.subtitle')}
          </div>
        </div>
        <button type="button" onClick={() => setAddOpen(true)} style={addBtn}>
          <UserPlus size={16} /> {t('manage.members.add')}
        </button>
      </div>

      {error && (
        <div role="alert" style={{
          margin: '0 24px 16px', padding: '12px 16px', borderRadius: RADII.control,
          background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.fg}33`,
          color: SEMANTIC.errore.fg, fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: COLORS.field }}>
            <th style={th}>{t('shared.name')}</th>
            <th style={th}>{t('shared.email')}</th>
            <th style={th}>{t('shared.status')}</th>
            <th style={th}>{t('users.columns.createdAt')}</th>
            <th style={{ ...th, textAlign: 'right' }} />
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td style={{ ...td, textAlign: 'center', color: COLORS.textTertiary }} colSpan={5}>
                {t('shared.loading')}
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td style={{ ...td, textAlign: 'center', color: COLORS.textTertiary }} colSpan={5}>
                {t('shared.emptyResult')}
              </td>
            </tr>
          )}
          {!loading && rows.map(row => {
            const isSelf = row.user_id === currentUserId;
            const locked = lockReason(row);
            return (
              <tr key={row.user_id}>
                {/* first_name / last_name since BE 56664fc (docs/frontend/2026-10-07-company-admins-names-handoff.md);
                    «—» when the person set no name. */}
                <td style={td}>{[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}</td>
                <td style={td}>{row.email}</td>
                <td style={td}>
                  {/* Two states here, not the users table's three: an outstanding invitation has no row at all. */}
                  <UserStatusPill isActive={row.is_active !== false} />
                </td>
                <td style={{ ...td, color: COLORS.textSecondary, whiteSpace: 'nowrap' }}>
                  {row.assigned_at ? new Date(row.assigned_at).toLocaleDateString('it-IT') : '—'}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {/* ⚠️ Absent, not greyed, on your own row: the backend answers 409 CANNOT_EDIT_OWN_EMAIL
                        and your own address is changed from Profilo. */}
                    {!isSelf && (
                      <button
                        type="button"
                        onClick={() => setEmailTarget(row)}
                        style={{ ...rowBtn('outline'), ...muted(false) }}
                      >
                        {t('manage.correctEmail')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => { if (!locked) setRemoveTarget(row); }}
                      disabled={!!locked}
                      title={locked || undefined}
                      style={{ ...rowBtn('danger'), ...muted(!!locked) }}
                    >
                      {t('shared.remove')}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <AddCompanyAdminDialog open={addOpen} onSubmit={submitAdd} onClose={() => setAddOpen(false)} />

      <ConfirmDialog
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        title={t('manage.members.removeDialogTitle')}
        message={removeTarget ? t('manage.members.removeTitle', { email: removeTarget.email }) : ''}
        confirmLabel={t('shared.remove')}
        variant="danger"
      />

      <CorrectEmailDialog
        open={!!emailTarget}
        currentEmail={emailTarget?.email}
        onSubmit={submitCorrectEmail}
        onClose={() => setEmailTarget(null)}
      />

      <OutcomeDialog outcome={outcome} onClose={() => setOutcome(null)} />
    </div>
  );
}
